<template>
  <div
    class="modal-card"
    style="width: 1200px; height: 90vh; display: flex; flex-direction: column"
  >
    <header class="modal-card-head" style="align-items: center">
      <p class="modal-card-title">
        {{ clubToEdit ? "Editar Club" : "Crear Club" }}
      </p>
      <b-button
        v-if="!clubToEdit"
        size="is-small"
        type="is-info"
        icon-left="copy"
        class="mr-4"
        @click="openCopyModal"
      >
        Copiar Club Existente
      </b-button>
      <button
        type="button"
        class="delete"
        @click="$emit('close')"
        aria-label="close"
      ></button>
    </header>

    <section class="modal-card-body" style="flex: 1; overflow-y: auto">
      <div v-if="loadingSchema" class="has-text-centered p-6">
        <b-icon icon="spinner" size="is-large" custom-class="fa-spin"></b-icon>
        <p class="mt-2">Cargando formulario dinámico...</p>
      </div>

      <form v-else @submit.prevent="save">
        <div class="columns is-multiline">
          <!-- Special handling for Logo and LogoBase64 to show at top with preview -->
          <div class="column is-12" v-if="validSchemaFields['Logo']">
            <b-field label="Logo">
              <b-input
                v-model="form['Logo']"
                placeholder="URL del Logo"
              ></b-input>
            </b-field>
            <div v-if="form['Logo']" class="has-text-centered my-2">
              <img
                :src="form['Logo']"
                style="max-height: 100px; max-width: 100%; object-fit: contain"
                alt="Logo Preview"
                onerror="this.style.display='none'"
              />
            </div>
          </div>

          <div class="column is-12" v-if="validSchemaFields['LogoBase64']">
            <b-field label="LogoBase64">
              <b-input
                v-model="form['LogoBase64']"
                placeholder="Base64 del Logo"
              ></b-input>
            </b-field>
            <div v-if="form['LogoBase64']" class="has-text-centered my-2">
              <img
                :src="form['LogoBase64']"
                style="max-height: 100px; max-width: 100%; object-fit: contain"
                alt="LogoBase64 Preview"
                onerror="this.style.display='none'"
              />
            </div>
          </div>

          <!-- Iterate over fields. Using key as the label strictly as requested -->
          <!-- Filter out Logo and LogoBase64 from general loop -->
          <div
            v-for="(field, key) in validSchemaFields"
            :key="key"
            class="column is-4"
            v-if="key !== 'Logo' && key !== 'LogoBase64' && key !== 'Id'"
          >
            <!-- Boolean (Checkbox/Switch) -->
            <b-field v-if="field.type === 'Boolean'" :label="field.name">
              <b-switch v-model="form[key]">
                {{ form[key] ? "Si" : "No" }}
              </b-switch>
            </b-field>

            <!-- Enum Select -->
            <b-field
              v-else-if="field.enum && field.enum.length > 0"
              :label="field.name"
            >
              <b-select v-model="form[key]" expanded :placeholder="field.name">
                <option :value="null">Ninguno</option>
                <option v-for="opt in field.enum" :value="opt" :key="opt">
                  {{ opt }}
                </option>
              </b-select>
            </b-field>

            <!-- Number -->
            <b-field v-else-if="field.type === 'Number'" :label="field.name">
              <b-input
                v-model.number="form[key]"
                type="number"
                :placeholder="field.name"
              ></b-input>
            </b-field>

            <!-- Color -->
            <b-field v-else-if="isColorField(key)" :label="field.name">
              <div style="display: flex; gap: 10px">
                <b-input
                  v-model="form[key]"
                  expanded
                  :placeholder="field.name"
                  @input="(val) => validateColor(key, val)"
                ></b-input>
                <input
                  type="color"
                  v-model="form[key]"
                  style="height: 40px; width: 60px; cursor: pointer"
                />
              </div>
            </b-field>

            <!-- String / Array / Object (Default to Text) -->
            <b-field v-else :label="field.name">
              <b-input v-model="form[key]" :placeholder="field.name"></b-input>
            </b-field>
          </div>
        </div>
      </form>

      <!-- Distributors (solo en edicion) -->
      <div v-if="clubToEdit" class="mt-5">
        <hr />
        <div class="is-flex is-justify-content-space-between is-align-items-center mb-3">
          <p class="title is-5 mb-0">Distributors</p>
          <b-button
            type="is-success"
            icon-left="plus"
            icon-pack="fas"
            size="is-small"
            @click="openDistributorForm(null)"
          >
            Agregar
          </b-button>
        </div>

        <b-table
          :data="distributors"
          :loading="loadingDistributors"
          :striped="true"
          :hoverable="true"
          :narrowed="true"
          :mobile-cards="true"
        >
          <b-table-column field="Nombre" label="Nombre" v-slot="props">
            {{ props.row.Nombre }}
          </b-table-column>

          <b-table-column field="Activo" label="Estado" centered v-slot="props">
            <b-tag :type="props.row.Activo ? 'is-success' : 'is-danger'">
              {{ props.row.Activo ? "Activo" : "Inactivo" }}
            </b-tag>
          </b-table-column>

          <b-table-column label="Acciones" centered v-slot="props">
            <b-button
              type="is-warning is-light"
              icon-left="pen"
              icon-pack="fas"
              size="is-small"
              class="mr-2"
              @click="openDistributorForm(props.row)"
            ></b-button>
            <b-button
              type="is-danger is-light"
              icon-left="trash"
              icon-pack="fas"
              size="is-small"
              @click="confirmDeleteDistributor(props.row)"
            ></b-button>
          </b-table-column>

          <template #empty>
            <div class="has-text-centered">Sin distributors</div>
          </template>
        </b-table>

        <!-- Form add/edit distributor -->
        <div v-if="distributorFormOpen" class="box mt-3">
          <p class="subtitle is-6">
            {{ distributorForm._id ? "Editar Distributor" : "Nuevo Distributor" }}
          </p>
          <div class="columns">
            <div class="column is-6">
              <b-field label="Nombre">
                <b-input v-model="distributorForm.Nombre"></b-input>
              </b-field>
            </div>
            <div class="column is-3">
              <b-field label="Activo">
                <b-switch v-model="distributorForm.Activo">
                  {{ distributorForm.Activo ? "Si" : "No" }}
                </b-switch>
              </b-field>
            </div>
            <div class="column is-3 is-flex is-align-items-flex-end">
              <b-button
                type="is-success"
                size="is-small"
                class="mr-2"
                :loading="savingDistributor"
                @click="saveDistributor"
              >
                Guardar
              </b-button>
              <b-button size="is-small" @click="distributorFormOpen = false">
                Cancelar
              </b-button>
            </div>
          </div>
        </div>
      </div>
    </section>
    <footer class="modal-card-foot" style="justify-content: flex-end">
      <b-button label="Cancelar" @click="$emit('close')" />
      <b-button
        :label="clubToEdit ? 'Actualizar' : 'Guardar'"
        type="is-success"
        @click="save"
        :loading="saving"
      />
    </footer>

    <!-- Modal for Copying Club -->
    <b-modal
      v-model="isCopyModalActive"
      has-modal-card
      trap-focus
      aria-role="dialog"
      aria-modal
      width="600"
    >
      <div class="modal-card" style="width: 600px; height: 60vh">
        <header class="modal-card-head">
          <p class="modal-card-title">Seleccionar Club para Copiar</p>
          <button
            type="button"
            class="delete"
            @click="isCopyModalActive = false"
          ></button>
        </header>
        <section class="modal-card-body">
          <b-field label="Buscar por nombre">
            <b-input
              v-model="searchQuery"
              placeholder="Escribe el nombre del club..."
              icon="search"
            ></b-input>
          </b-field>
          <b-table
            :data="filteredClubs"
            :paginated="true"
            :per-page="10"
            :striped="true"
            :hoverable="true"
            @click="selectClubToCopy"
            style="cursor: pointer"
          >
            <b-table-column
              field="Nombre"
              label="Nombre"
              sortable
              v-slot="props"
            >
              {{ props.row.Nombre }}
            </b-table-column>
            <b-table-column
              field="UrlLogin"
              label="Url Login"
              sortable
              v-slot="props"
            >
              {{ props.row.UrlLogin || "-" }}
            </b-table-column>
            <template #empty>
              <div class="has-text-centered">No se encontraron clubes.</div>
            </template>
          </b-table>
        </section>
        <footer class="modal-card-foot" style="justify-content: flex-end">
          <b-button label="Cerrar" @click="isCopyModalActive = false" />
        </footer>
      </div>
    </b-modal>
  </div>
</template>

<script>
import axios from "axios";
const API_URL = process.env.VUE_APP_API_URL;

export default {
  name: "ModalCreateClub",
  props: {
    clubToEdit: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      loadingSchema: true,
      saving: false,
      validSchemaFields: {},
      form: {},
      // Copy Club Logic
      isCopyModalActive: false,
      clubsList: [],
      searchQuery: "",
      // Distributors
      distributors: [],
      loadingDistributors: false,
      distributorFormOpen: false,
      savingDistributor: false,
      distributorForm: { Nombre: "", Activo: true },
    };
  },
  computed: {
    filteredClubs() {
      if (!this.searchQuery) {
        return this.clubsList;
      }
      const lower = this.searchQuery.toLowerCase();
      return this.clubsList.filter((club) =>
        club.Nombre.toLowerCase().includes(lower),
      );
    },
  },
  async created() {
    await this.fetchSchema();
    if (this.clubToEdit) {
      this.populateForm();
      this.loadDistributors();
    }
  },
  methods: {
    isColorField(key) {
      return (
        key.toLowerCase().includes("color") && key.toLowerCase().includes("hex")
      );
    },
    validateColor(key, value) {
      if (value && !value.startsWith("#")) {
        this.form[key] = "#" + value;
      }
    },
    async fetchSchema() {
      try {
        this.loadingSchema = true;
        const { data } = await axios.get(`${API_URL}/system/club/schema`);
        this.parseSchema(data);
        this.loadingSchema = false;
      } catch (error) {
        console.error("Error fetching schema:", error);
        this.$buefy.toast.open({
          message: "Error al cargar el esquema del formulario",
          type: "is-danger",
        });
        this.loadingSchema = false;
        this.$emit("close");
      }
    },
    parseSchema(schema) {
      // The schema is returned as an array of objects: [{ name: '...', type: '...' }, ...]
      const fields = {};

      if (Array.isArray(schema)) {
        schema.forEach((fieldDef) => {
          const key = fieldDef.name;
          const type = fieldDef.type;
          const enumValues = fieldDef.enum || [];

          fields[key] = {
            name: key,
            type: type,
            enum: enumValues,
          };

          // Initialize form value
          if (!(key in this.form)) {
            if (type === "Boolean") this.$set(this.form, key, false);
            else if (type === "Number") this.$set(this.form, key, null);
            else if (type === "Array") this.$set(this.form, key, []);
            else this.$set(this.form, key, "");
          }
        });
      }

      this.validSchemaFields = fields;
    },
    populateForm() {
      for (const key in this.validSchemaFields) {
        if (this.clubToEdit[key] !== undefined) {
          let value = this.clubToEdit[key];
          // Ensure color fields start with #
          if (
            this.isColorField(key) &&
            value &&
            typeof value === "string" &&
            !value.startsWith("#")
          ) {
            value = "#" + value;
          }
          this.form[key] = value;
        }
      }
      if (this.clubToEdit._id) {
        this.form._id = this.clubToEdit._id;
      }
    },
    async save() {
      try {
        this.saving = true;

        // Create a copy of the form for submission
        const payload = { ...this.form };

        // Strip '#' from color fields
        for (const key in payload) {
          if (
            this.isColorField(key) &&
            payload[key] &&
            typeof payload[key] === "string" &&
            payload[key].startsWith("#")
          ) {
            payload[key] = payload[key].substring(1);
          }
        }

        if (this.clubToEdit) {
          // Update
          await axios.put(
            `${API_URL}/system/club/update/${this.clubToEdit._id}`,
            payload,
          );
          this.$buefy.toast.open({
            message: "Club actualizado correctamente",
            type: "is-success",
          });
        } else {
          // Create
          // Generate a random UUID for the Id field if not present
          if (!payload.Id) {
            payload.Id = crypto.randomUUID();
          }

          await axios.post(`${API_URL}/system/club/create`, payload);
          this.$buefy.toast.open({
            message: "Club creado correctamente",
            type: "is-success",
          });
        }

        this.$emit("refresh");
        this.$emit("close");
      } catch (error) {
        console.error("Error saving club:", error);
        this.$buefy.toast.open({
          message: "Error al guardar el club",
          type: "is-danger",
        });
      } finally {
        this.saving = false;
      }
    },
    async openCopyModal() {
      if (this.clubsList.length === 0) {
        try {
          const { data } = await axios.get(`${API_URL}/system/club/list`);
          if (Array.isArray(data)) {
            this.clubsList = data;
          } else if (data && Array.isArray(data.clubs)) {
            this.clubsList = data.clubs;
          } else if (data && Array.isArray(data.data)) {
            this.clubsList = data.data;
          }
        } catch (error) {
          console.error("Error loading clubs list:", error);
          this.$buefy.toast.open({
            message: "Error al cargar la lista de clubes",
            type: "is-danger",
          });
        }
      }
      this.isCopyModalActive = true;
    },
    async loadDistributors() {
      const clubId = this.clubToEdit?.Id;
      if (!clubId) return;
      this.loadingDistributors = true;
      try {
        const { data } = await axios.get(`${API_URL}/system/distributor/list`, {
          params: { ClubId: clubId, Estado: "Ambos" },
        });
        this.distributors = data?.data || [];
      } catch (err) {
        console.error("Error loading distributors:", err);
        this.$buefy.toast.open({
          message: "Error al cargar distributors",
          type: "is-danger",
        });
      } finally {
        this.loadingDistributors = false;
      }
    },
    openDistributorForm(row) {
      if (row) {
        this.distributorForm = { ...row };
      } else {
        this.distributorForm = { Nombre: "", Activo: true };
      }
      this.distributorFormOpen = true;
    },
    async saveDistributor() {
      const clubId = this.clubToEdit?.Id;
      if (!clubId) {
        this.$buefy.toast.open({
          message: "Guarda primero el club",
          type: "is-warning",
        });
        return;
      }
      if (!this.distributorForm.Nombre) {
        this.$buefy.toast.open({
          message: "Nombre requerido",
          type: "is-warning",
        });
        return;
      }
      this.savingDistributor = true;
      try {
        if (this.distributorForm._id) {
          await axios.put(
            `${API_URL}/system/distributor/update/${this.distributorForm._id}`,
            {
              Nombre: this.distributorForm.Nombre,
              Activo: this.distributorForm.Activo,
            },
          );
        } else {
          await axios.post(`${API_URL}/system/distributor/create`, {
            Id: crypto.randomUUID(),
            ClubId: clubId,
            Nombre: this.distributorForm.Nombre,
            Activo: this.distributorForm.Activo,
          });
        }
        this.distributorFormOpen = false;
        await this.loadDistributors();
        this.$buefy.toast.open({
          message: "Distributor guardado",
          type: "is-success",
        });
      } catch (err) {
        console.error("Error saving distributor:", err);
        this.$buefy.toast.open({
          message: "Error al guardar distributor",
          type: "is-danger",
        });
      } finally {
        this.savingDistributor = false;
      }
    },
    confirmDeleteDistributor(row) {
      this.$buefy.dialog.confirm({
        title: "Eliminar Distributor",
        message: `¿Eliminar <b>${row.Nombre}</b>?`,
        confirmText: "Eliminar",
        type: "is-danger",
        hasIcon: true,
        onConfirm: async () => {
          try {
            await axios.delete(
              `${API_URL}/system/distributor/delete/${row._id}`,
            );
            await this.loadDistributors();
            this.$buefy.toast.open({
              message: "Distributor eliminado",
              type: "is-success",
            });
          } catch (err) {
            console.error("Error deleting distributor:", err);
            this.$buefy.toast.open({
              message: "Error al eliminar",
              type: "is-danger",
            });
          }
        },
      });
    },
    selectClubToCopy(club) {
      // Copy fields from selected club to form
      // Similar reasoning as populateForm, but without ID
      for (const key in this.validSchemaFields) {
        if (club[key] !== undefined) {
          let value = club[key];
          // Handle color format
          if (
            this.isColorField(key) &&
            value &&
            typeof value === "string" &&
            !value.startsWith("#")
          ) {
            value = "#" + value;
          }
          this.$set(this.form, key, value);
        }
      }
      // Ensure we DO NOT copy the _id or specific unique identifiers if they shouldn't be duplicated (like Id)
      // The user wants to copy data to create NEW club.
      // Reset critical unique fields:
      if (this.form._id) delete this.form._id;
      // We might want to clear 'Id' (UUID) to let it regenerate or user input?
      // User request implies "rellena todos los campos con los datos".
      // Usually unique IDs like 'Id' should be new.
      // Let's clear 'Id' so our save logic generates a new one.
      if (this.form.Id) delete this.form.Id;

      this.isCopyModalActive = false;
      this.$buefy.toast.open({
        message: "Datos copiados. Verifique y guarde el nuevo club.",
        type: "is-success",
      });
    },
  },
};
</script>

<style scoped>
.modal-card {
  border-radius: 6px;
}
</style>
